import { ShapeCombo, ShapeComboOptions, ShapeOptions } from "../Shape";
import RingShape from "./RingShape";
import LineShape from "./LineShape";
import { toRadian } from "../../../math";

/**
 * CSS 坐标系
 * 0deg = 从下到上
 * 90deg = 从左到右
 *                   [0deg]            [45deg]
 *                      ⬆            ⬈
 *                      *         *
 *                      *     *
 *                      *  *
 *  [-90deg] ⬅ *  *  *  *  *  *  * ⮕  [90deg]
 *                      *
 *                      *
 *                      *
 *                      ⬇
 *                   [180deg]
 */

/**
 * canvas svg Vector 坐标系
 *                [-90° / 270°]
 *                  *
 *                  *
 *                  *
 *  [180°] *  *  *  *  *  *  *  [0°]
 *                  *
 *                  *
 *                  *
 *                [90°]
 */

export type RingIndicatorShapeComboOptions = {
  half?: boolean; // 半圆 或 全圆
  lineWidth?: number;
  width: number;
  height: number;
  startDegree?: number;
  endDegree?: number;
  bgColor: string;
  startColor: string;
  endColor: string;
  percent?: number;
} & ShapeComboOptions;

export class RingIndicatorShapeCombo extends ShapeCombo {
  public half: boolean;
  public lineWidth: number;
  public width: number;
  public height: number;

  public bgColor: string;
  public startColor: string;
  public endColor: string;

  public startDegree: number;
  public endDegree: number;

  public percent = 0;

  public ringBg: RingShape;
  public ring: RingShape;
  public lines: LineShape[] = [];
  public indicatorLineCount: number;

  constructor(options: RingIndicatorShapeComboOptions) {
    super(options);

    this.half = typeof options?.half == "boolean" ? options?.half : false;
    this.lineWidth = options?.lineWidth || 12;
    this.width = options.width;
    this.height = options.height;

    this.bgColor = options?.bgColor;
    this.startColor = options?.startColor;
    this.endColor = options?.endColor;

    if (this.half) {
      this.startDegree = options?.startDegree || -90 - 15;
      this.endDegree = options?.endDegree || 90 + 15;
    } else {
      this.startDegree = options?.startDegree || 0;
      this.endDegree = options?.endDegree || 360;
    }

    this.percent = options?.percent || 0;

    this.ringBg = new RingShape({
      half: this.half,
      lineWidth: this.lineWidth,
      width: options.width,
      height: options.height,
      startDegree: this.startDegree,
      endDegree: this.endDegree,
      startColor: this.bgColor,
      percent: 1
    });
    this.addShape(this.ringBg);

    this.ring = new RingShape({
      half: this.half,
      lineWidth: this.lineWidth,
      width: options.width,
      height: options.height,
      startDegree: this.startDegree,
      endDegree: this.endDegree,
      startColor: this.startColor,
      endColor: this.endColor,
      percent: 0
    });
    this.addShape(this.ring);

    this.indicatorLineCount = 22 + 1;

    if (this.half) {
      const indicatorLineToRingDistance = 20;
      const indicatorLineHeight = 17;
      const indicatorLineWidth = 2;
      const length =
        this.ringBg.radius - indicatorLineToRingDistance - indicatorLineHeight;
      for (let i = 0; i < this.indicatorLineCount; i++) {
        const line = new LineShape({
          lineLength: indicatorLineHeight,
          startColor: this.startColor,
          endColor: this.endColor,
          attrs: {
            "stroke-linecap": "round",
            "stroke-width": String(indicatorLineWidth),
            stroke: this.bgColor
          }
        });
        this.lines.push(line);
        const lineDegree =
          -90 +
          this.startDegree +
          ((this.endDegree - this.startDegree) /
            (this.indicatorLineCount - 1)) *
            i;
        this.addShape(line, {
          dX: this.ringBg.x + Math.cos(toRadian(lineDegree)) * length,
          dY: this.ringBg.y + Math.sin(toRadian(lineDegree)) * length,
          dDegree: lineDegree
        });
      }
    }

    // 默认 0%
    this.setPercent(options.percent || 0);
  }

  protected cloneShapeCombo(): RingIndicatorShapeCombo {
    const shapeCombo = new RingIndicatorShapeCombo({
      ...super.getBaseOptions(),
      half: this.half,
      lineWidth: this.lineWidth,
      width: this.width,
      height: this.height,
      bgColor: this.bgColor,
      startColor: this.startColor,
      endColor: this.endColor,
      startDegree: this.startDegree,
      endDegree: this.endDegree,
      percent: this.percent
    });
    return shapeCombo;
  }

  // 提供外部变更
  public setPercent(percent: number) {
    this.percent = Math.max(0, percent);
    this.ring.setPercent(percent);
    if (this.half) {
      for (let i = 0; i < this.indicatorLineCount; i++) {
        const isInPercent = i / (this.indicatorLineCount - 1) <= this.percent;
        if (isInPercent) {
          this.lines[i].enableGradient(this.startColor, this.endColor);
        } else {
          this.lines[i].disableGradient(this.bgColor);
        }
      }
    }
  }
}

export default RingIndicatorShapeCombo;
